// =============================================
// Unsaved-changes guard — app-styled Save / Discard / Cancel.
// =============================================
// Replaces the old native window.confirm with an in-app modal (see
// src/components/ui/UnsavedChangesModal.tsx, mounted once globally).
//
// Flow: a form marks itself dirty (setFormDirty) and optionally registers how to
// save itself (registerSaveHandler). Any navigation/tab handler calls
// `await confirmDiscardChanges()` before navigating:
//   - not dirty                 → resolves true immediately (no modal)
//   - dirty → modal opens:
//       Save    → runs the registered save handler; true if it succeeds
//       Discard → drops changes; true
//       Cancel  → false (navigation aborts; stay put)
// "Save" only appears when a save handler is registered for the current form.
// =============================================

export type UnsavedChoice = 'save' | 'discard' | 'cancel';

let _dirty = false;
// The current dirty form's save fn — returns true on success (false = validation/error → stay).
let _saveHandler: (() => boolean | Promise<boolean>) | null = null;
// Optional: extra cleanup the current form needs when the user picks "Discard"
// (e.g. drop its autosaved draft so the discarded edits don't come back).
let _discardHandler: (() => void) | null = null;
// Optional: a form that keeps its unsaved edits as a DRAFT on the server (Process Docs
// Filing) registers how to send that draft now. Leaving such a form loses nothing, so
// the guard sends the draft and lets the navigation through — no prompt.
let _autosaveHandler: (() => void | Promise<void>) | null = null;
let _editSeq = 0;

interface PendingRequest {
  message: string;
  canSave: boolean;
  resolve: (choice: UnsavedChoice) => void;
}
let _pending: PendingRequest | null = null;

const _listeners = new Set<() => void>();
function emit() { _listeners.forEach(l => l()); }

/** Subscribe the modal component to pending-request changes. Returns an unsubscribe. */
export function subscribeUnsaved(listener: () => void): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}
/** The modal reads this to know whether/what to render. */
export function getPendingUnsaved(): PendingRequest | null { return _pending; }
/** The modal calls this when the user picks a button. */
export function resolveUnsaved(choice: UnsavedChoice): void { _pending?.resolve(choice); }

export function setFormDirty(dirty: boolean): void { _dirty = dirty; if (dirty) _editSeq++; }
export function isFormDirty(): boolean { return _dirty; }
/**
 * Counts every "the user just edited something" signal (each setFormDirty(true)).
 * A form that autosaves drafts compares this between renders to tell a real user
 * edit apart from its own programmatic field changes (pre-fill, auto-fill).
 */
export function getEditSeq(): number { return _editSeq; }

/** A form registers how to save itself (called when "Save" is clicked). Pass null to clear. */
export function registerSaveHandler(fn: (() => boolean | Promise<boolean>) | null): void {
  _saveHandler = fn;
}

/** A form registers cleanup to run when "Discard changes" is clicked. Pass null to clear. */
export function registerDiscardHandler(fn: (() => void) | null): void {
  _discardHandler = fn;
}

/** A draft-autosaving form registers how to flush its draft now. Pass null to clear. */
export function registerAutosaveHandler(fn: (() => void | Promise<void>) | null): void {
  _autosaveHandler = fn;
}

/**
 * Build a modal save handler from a form's existing submit fn (FormEvent → void/Promise).
 * Relies on the sitewide rule that a successful submit clears the dirty flag — so if the
 * flag is still set after running, the submit failed validation (→ false = abort the nav).
 * Lets any form wire "Save & continue" without refactoring its submit's internal returns.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function submitSaver(submit: (e: any) => unknown): () => Promise<boolean> {
  return async () => {
    await Promise.resolve(submit({ preventDefault() {} }));
    return !_dirty;
  };
}

/**
 * Guard a navigation. Resolves true if it's safe to proceed (not dirty, saved, or
 * discarded), false if the user cancelled. Awaits the in-app modal when dirty.
 */
export async function confirmDiscardChanges(message?: string): Promise<boolean> {
  if (!_dirty) return true;
  if (_pending) return false; // a prompt is already open — ignore the new nav
  if (_autosaveHandler) {
    // the edits are a draft that is kept — send it now and go; they come back on return
    try { await Promise.resolve(_autosaveHandler()); } catch { /* the debounced copy still goes */ }
    _dirty = false;
    return true;
  }

  const choice = await new Promise<UnsavedChoice>(resolve => {
    _pending = {
      message: message || 'You have unsaved changes.',
      canSave: !!_saveHandler,
      resolve,
    };
    emit();
  });
  _pending = null;
  emit();

  if (choice === 'cancel') return false;
  if (choice === 'discard') {
    try { _discardHandler?.(); } catch { /* cleanup is best-effort */ }
    _dirty = false;
    return true;
  }
  // 'save'
  try {
    const ok = await Promise.resolve(_saveHandler ? _saveHandler() : true);
    if (ok) { _dirty = false; return true; }
    return false; // save failed / validation error → stay so the user can fix it
  } catch {
    return false;
  }
}
